/**
=========================================================
* Soft UI Dashboard PRO React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiBadge from "components/SuiBadge";

// Timeline context
import { useTimeline } from "examples/Timeline/context";

// Custom styles for the TimelineItem
import { timelineItem, timelineItemIcon } from "examples/Timeline/TimelineItem/styles";
import { capitalCase } from "capital-case";
function TimelineItem({
  color,
  icon,
  title,
  dateTime,
  description,
  badges,
  lastItem,
  badgeTitle,
  isEmpty,
}) {
  const isDark = useTimeline();

  const renderBadges =
    badges.length > 0
      ? badges.map((badge, key) => {
          const badgeKey = `badge-${key}`;

          return (
            <SuiBox key={badgeKey} mr={key === badges.length - 1 ? 0 : 0.5} mb={1}>
              <SuiBadge
                textTransform="none"
                color={color}
                size="xs"
                badgeContent={capitalCase(badge, { stripRegexp: /[^A-Z0-9\'\&\/\-_]/gi })}
                container
              />
            </SuiBox>
          );
        })
      : null;

  return (
    <SuiBox position="relative" sx={(theme) => timelineItem(theme, { lastItem })}>
      <SuiBox
        bgColor={isDark ? "dark" : "white"}
        width="1.625rem"
        height="1.625rem"
        borderRadius="50%"
        position="absolute"
        top="3.25%"
        left="2px"
        zIndex={2}
      >
        <Icon
          fontSize="small"
          sx={(theme) => timelineItemIcon(theme, { color: isEmpty ? "secondary" : color })}
        >
          {icon}
        </Icon>
      </SuiBox>
      <SuiBox ml={5.75} pt={description ? 0.7 : 0.5} lineHeight={0} maxWidth="40rem">
        <SuiTypography
          variant="button"
          fontWeight="medium"
          fontSize="large"
          color={isEmpty ? "secondary" : "dark"}
        >
          {title}
        </SuiTypography>
        {dateTime ? (
          <SuiBox mt={0.5}>
            <SuiTypography
              variant="caption"
              fontWeight="medium"
              color={isDark ? "secondary" : "text"}
            >
              {dateTime}
            </SuiTypography>
          </SuiBox>
        ) : null}
        <SuiBox mt={2} mb={1.5}>
          {description ? (
            <SuiTypography
              variant="button"
              fontWeight="regular"
              color="text"
              className="min-h-[80px]"
            >
              <div className="overflow-y-auto max-h-[80px]">{description}</div>
            </SuiTypography>
          ) : null}
        </SuiBox>
        {badges.length > 0 ? (
          <SuiBox display="flex" pb={lastItem ? 1 : 2}>
            {badgeTitle ? (
              <SuiTypography
                variant="button"
                fontWeight="medium"
                fontSize="small"
                mr={1}
                color={isDark ? "white" : "dark"}
              >
                {badgeTitle}
              </SuiTypography>
            ) : null}
            <div className="flex flex-wrap">{renderBadges}</div>
          </SuiBox>
        ) : null}
      </SuiBox>
    </SuiBox>
  );
}

// Setting default values for the props of TimelineItem
TimelineItem.defaultProps = {
  color: "info",
  badges: [],
  lastItem: false,
  description: "",
  badgeTitle: "",
  isEmpty: false,
};

// Typechecking props for the TimelineItem
TimelineItem.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
    "themecolor1",
    "themecolor2",
    "themecolor3",
    "themecolor4",
    "themecolor5",
  ]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  dateTime: PropTypes.string,
  description: PropTypes.string,
  badges: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  lastItem: PropTypes.bool,
  badgeTitle: PropTypes.string,
  isEmpty: PropTypes.bool,
};

export default TimelineItem;
